import { Web } from "sip.js";
import {
  Inviter,
  Registerer,
  RegistererState,
  SessionState,
  SIPExtension,
  TransportState,
  UserAgent,
} from "sip.js";
import { primeMicrophoneAccess } from "./microphoneAccess";
import {
  releaseMicrophoneStream,
  sharedMicrophoneStreamFactory,
} from "./microphoneStreamFactory";
import {
  clearBrokenEarlyDialog,
  invitationRequiresReliableProgress,
  isInvitationAlive,
} from "./incomingCallFlow";
import { sessionOptionsWithSdpModifiers, WEBRTC_SDP_MODIFIERS } from "./sdpModifiers";

const { defaultSessionDescriptionHandlerFactory } = Web;

const ICE_GATHERING_TIMEOUT_MS = 20000;

/**
 * Prefer G.711 before createOffer/createAnswer so UCM PSTN bridging stays stable
 * without rewriting SDP (rewrites previously produced invalid answers).
 */
const preferG711Codecs = (peerConnection) => {
  if (!peerConnection || typeof RTCRtpReceiver === "undefined") {
    return;
  }
  if (typeof RTCRtpReceiver.getCapabilities !== "function") {
    return;
  }
  const capabilities = RTCRtpReceiver.getCapabilities("audio");
  if (!capabilities?.codecs?.length) {
    return;
  }
  const preferred = [
    ...capabilities.codecs.filter((codec) => /pcmu/i.test(codec.mimeType)),
    ...capabilities.codecs.filter((codec) => /pcma/i.test(codec.mimeType)),
    ...capabilities.codecs.filter((codec) => /telephone-event/i.test(codec.mimeType)),
    ...capabilities.codecs.filter(
      (codec) => !/pcmu|pcma|telephone-event|rtx|red|ulpfec/i.test(codec.mimeType)
    ),
  ];
  peerConnection.getTransceivers().forEach((transceiver) => {
    if (typeof transceiver.setCodecPreferences !== "function") {
      return;
    }
    try {
      transceiver.setCodecPreferences(preferred);
    } catch {
      // Browser may reject preferences; fall back to default offer/answer.
    }
  });
};

const createUcmSessionDescriptionHandlerFactory = () => {
  const baseFactory = defaultSessionDescriptionHandlerFactory(sharedMicrophoneStreamFactory);
  return (session, options) => {
    const handler = baseFactory(session, options);
    const originalCreate = handler.createLocalOfferOrAnswer.bind(handler);
    handler.createLocalOfferOrAnswer = (createOptions) => {
      preferG711Codecs(handler.peerConnection);
      return originalCreate(createOptions);
    };
    return handler;
  };
};

/**
 * Audio session options for on-prem UCM WebRTC.
 * Remote UCM SDP is sanitized; local codec preference is via setCodecPreferences.
 */
const audioSessionOptions = () =>
  sessionOptionsWithSdpModifiers({
    sessionDescriptionHandlerOptions: {
      constraints: { audio: true, video: false },
      iceGatheringTimeout: ICE_GATHERING_TIMEOUT_MS,
    },
  });

const applySessionModifiers = (session) => {
  session.sessionDescriptionHandlerModifiers = WEBRTC_SDP_MODIFIERS;
  session.sessionDescriptionHandlerModifiersReInvite = WEBRTC_SDP_MODIFIERS;
  session.sessionDescriptionHandlerOptions = {
    constraints: { audio: true, video: false },
    iceGatheringTimeout: ICE_GATHERING_TIMEOUT_MS,
  };
};

/**
 * Dial URI for UCM outbound routes. Keep digits and optional leading +.
 * Wave dials the same form the UCM outbound route expects.
 */
const normalizeDialTarget = (phoneNumber, sipDomain) => {
  const raw = String(phoneNumber || "").trim();
  if (!raw) {
    throw new Error("Phone number is required");
  }
  if (raw.startsWith("sip:")) {
    return UserAgent.makeURI(raw);
  }
  const digits = raw.replace(/[^\d+]/g, "");
  return UserAgent.makeURI(`sip:${digits}@${sipDomain}`);
};

const buildIceServers = (iceServers, sipDomain) => {
  const configured = (Array.isArray(iceServers) ? iceServers : [])
    .filter((server) => server?.urls)
    .map((server) => ({
      urls: server.urls,
      username: server.username || undefined,
      credential: server.credential || undefined,
    }));

  // On-prem UCM often exposes STUN on the PBX itself — helps LAN ICE gathering.
  const lanStun =
    sipDomain && /^\d{1,3}(\.\d{1,3}){3}$/.test(String(sipDomain).trim())
      ? [{ urls: `stun:${String(sipDomain).trim()}:3478` }]
      : [];

  const merged = [...configured, ...lanStun];
  if (merged.length > 0) {
    return merged;
  }
  return [{ urls: "stun:stun.l.google.com:19302" }];
};

const registrationAuthError = (extension, statusCode, sipDomain) =>
  `SIP registration failed (${statusCode} unauthorized). ` +
  `UCM rejected the SIP digest for extension ${extension}. ` +
  `Use the extension SIP/IAX registration password from UCM (PBX → Extensions → ${extension} → ` +
  `Account — not the UCM admin/API password). Enable WebRTC globally and on this extension ` +
  `(Value-added Features → WebRTC). Under extension Media, set registration strategy to Allow All while testing. ` +
  `Confirm on the same PC: log in to GS Wave Web (https://${sipDomain || "ucm-ip"}:8090/gswave) with the same ` +
  `extension and SIP/IAX password. Update Agent SIP password in User Admin, then reset the phone.`;

const waitForSipRegistration = (registerer, extension) =>
  new Promise((resolve, reject) => {
    let settled = false;
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      registerer.stateChange.removeListener(onStateChange);
      reject(new Error("SIP registration timed out waiting for UCM response."));
    }, 15000);

    const finish = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      registerer.stateChange.removeListener(onStateChange);
      if (error) reject(error);
      else resolve();
    };

    const onStateChange = (state) => {
      if (state === RegistererState.Registered) {
        finish(null);
      }
    };

    registerer.stateChange.addListener(onStateChange);
    registerer.register({
      requestDelegate: {
        onReject: (response) => {
          const statusCode = response?.message?.statusCode || 0;
          if (statusCode === 401 || statusCode === 403) {
            finish(new Error(registrationAuthError(extension, statusCode, registerer?.userAgent?.configuration?.uri?.host)));
            return;
          }
          finish(new Error(`SIP registration failed (SIP ${statusCode || "unknown"}).`));
        },
      },
    });
  });

const isLiveSession = (session) =>
  Boolean(
    session &&
      session.state !== SessionState.Terminated &&
      session.state !== SessionState.Terminating
  );

/**
 * SIP.js client modeled on Grandstream Wave WebRTC behavior:
 * register over WSS, ring with 180, answer with G.711-first audio, keep hangup handlers active while ringing.
 */
export class SipClient {
  constructor(config, handlers = {}) {
    this.config = config;
    this.handlers = handlers;
    this.userAgent = null;
    this.registerer = null;
    this.session = null;
    this.remoteAudio = handlers.remoteAudio || null;
  }

  updateHandlers(handlers = {}) {
    this.handlers = { ...this.handlers, ...handlers };
    if (handlers.remoteAudio) {
      this.remoteAudio = handlers.remoteAudio;
    }
  }

  /**
   * Drop any non-live session left after a missed/failed call so outbound dial can proceed.
   */
  releaseStaleSession() {
    if (!this.session) {
      return;
    }
    if (!isLiveSession(this.session)) {
      this.session = null;
      return;
    }
    // Dead inbound invite that never terminated cleanly.
    if (typeof this.session.accept === "function" && !isInvitationAlive(this.session)) {
      this.session = null;
    }
  }

  /**
   * Tear down any non-established session (stuck ring / failed dial leftovers).
   */
  async clearIdleSession() {
    this.releaseStaleSession();
    if (!this.session || this.session.state === SessionState.Established) {
      return;
    }
    const session = this.session;
    this.session = null;
    try {
      const isOutboundInviter =
        typeof session.cancel === "function" && typeof session.accept !== "function";
      if (isOutboundInviter) {
        await session.cancel();
      } else if (typeof session.reject === "function") {
        await session.reject({ statusCode: 486 });
      } else if (typeof session.cancel === "function") {
        await session.cancel();
      }
    } catch {
      // ignore — session may already be gone
    }
    releaseMicrophoneStream();
  }

  /**
   * Hard reset used by Reset phone — ends any session including an established call.
   */
  async forceEndSession() {
    const session = this.session;
    if (!session) {
      releaseMicrophoneStream();
      this.stopRemoteAudio();
      return;
    }
    this.session = null;
    try {
      if (session.state === SessionState.Established && typeof session.bye === "function") {
        await session.bye();
      } else {
        const isOutboundInviter =
          typeof session.cancel === "function" && typeof session.accept !== "function";
        if (isOutboundInviter) {
          await session.cancel();
        } else if (typeof session.reject === "function") {
          await session.reject({ statusCode: 486 });
        } else if (typeof session.cancel === "function") {
          await session.cancel();
        }
      }
    } catch {
      // ignore teardown races
    }
    releaseMicrophoneStream();
    this.stopRemoteAudio();
  }

  async connect() {
    if (!this.config?.enableBrowserPhone || !this.config?.sipWebSocketUrl) {
      throw new Error(
        "Browser calling is not enabled. Configure UCM WebRTC/SIP WebSocket settings and agent extension credentials."
      );
    }

    const extension = String(
      this.config.extensionUsername || this.config.agentExtension || ""
    ).trim();
    const password = String(this.config.sipAuthorizationPassword || "").trim();
    const sipDomain = String(this.config.sipDomain || "").trim();
    if (!extension || !password) {
      throw new Error("Agent extension and SIP password are required for browser calling.");
    }
    if (!sipDomain) {
      throw new Error("SIP domain is required for browser calling.");
    }

    const uri = UserAgent.makeURI(`sip:${extension}@${sipDomain}`);
    if (!uri) {
      throw new Error("Invalid SIP URI for browser phone registration.");
    }

    const webSocketUrl = String(this.config.sipWebSocketUrl || "").trim();
    const sipTrace =
      typeof localStorage !== "undefined" && localStorage.getItem("EMMAD_SIP_TRACE") === "1";

    const authUsername = String(
      this.config.sipAuthorizationUsername || this.config.extensionUsername || extension
    ).trim();
    const isGrandstream = this.config.providerName === "GRANDSTREAM_UCM";
    // UCM WebRTC expects public identity sip:ext@domain; digest URI must match Request-URI.
    const registrarUri = isGrandstream
      ? `sip:${extension}@${sipDomain}`
      : `sip:${sipDomain}`;
    const registrar = UserAgent.makeURI(registrarUri);

    this.userAgent = new UserAgent({
      uri,
      authorizationUsername: authUsername,
      authorizationPassword: password,
      displayName: this.config.displayName || extension,
      userAgentString: "CallCenterBrowserPhone/2.1-ucm-wss",
      // Must be false: sip.js auto-180 before onInvite blocks our UI handler.
      sendInitialProvisionalResponse: false,
      sipExtension100rel: SIPExtension.Supported,
      sessionDescriptionHandlerFactory: createUcmSessionDescriptionHandlerFactory(),
      // UCM WebRTC Contact must advertise transport=ws even on wss://.
      contactParams: { transport: "ws" },
      transportOptions: {
        server: webSocketUrl,
        traceSip: sipTrace,
      },
      sessionDescriptionHandlerFactoryOptions: {
        constraints: {
          audio: true,
          video: false,
        },
        iceGatheringTimeout: ICE_GATHERING_TIMEOUT_MS,
        peerConnectionConfiguration: {
          iceServers: buildIceServers(this.config.iceServers, this.config.sipDomain),
        },
      },
    });

    this.userAgent.transport.stateChange.addListener((state) => {
      if (state !== TransportState.Disconnected || !this.handlers.onTransportError) {
        return;
      }
      this.handlers.onTransportError(
        new Error(
          `Cannot connect to ${webSocketUrl}. ` +
            "Confirm the Secure WebSocket Interface on the UCM (Value-added Features → HTTP & WebSocket). " +
            "Close Wave Desktop for this extension (UCM allows one active registration). " +
            "Open https://192.168.0.160:8089 in the browser first to trust the UCM certificate."
        )
      );
    });

    this.userAgent.delegate = {
      onInvite: (invitation) => {
        this.handleIncomingInvite(invitation);
      },
    };

    await this.userAgent.start();
    this.registerer = new Registerer(this.userAgent, registrar ? { registrar } : undefined);
    await waitForSipRegistration(this.registerer, extension);

    if (this.handlers.onRegistered) {
      this.handlers.onRegistered();
    }
  }

  handleIncomingInvite(invitation) {
    this.releaseStaleSession();
    if (isLiveSession(this.session)) {
      invitation.reject({ statusCode: 486 }).catch(() => undefined);
      return;
    }

    applySessionModifiers(invitation);
    this.session = invitation;
    this.attachSessionHandlers(invitation, "INBOUND");
    // Wave keeps CANCEL/BYE handlers active for the whole ringing period.
    this.bindSessionHangupHandlers(invitation, "INBOUND", null);

    // Send provisional responses before React UI work — UCM drops calls if 180/183 is delayed.
    const sendProvisionalResponse = async () => {
      if (!isInvitationAlive(invitation)) {
        return;
      }
      if (!invitationRequiresReliableProgress(invitation)) {
        await invitation.progress({ statusCode: 180 });
        return;
      }
      // UCM + 100rel: send 180 immediately (Wave-style), then 183+SDP after mic — do not block 180 on getUserMedia.
      await invitation.progress({ statusCode: 180 }).catch(() => undefined);
      if (!isInvitationAlive(invitation)) {
        return;
      }
      await primeMicrophoneAccess();
      await invitation.progress({ statusCode: 183, ...audioSessionOptions() });
    };

    sendProvisionalResponse().catch((error) => {
      if (this.handlers.onIncomingProgressFailed) {
        this.handlers.onIncomingProgressFailed({ invitation, error });
      }
    });

    primeMicrophoneAccess().catch(() => undefined);

    if (isInvitationAlive(invitation) && this.handlers.onIncomingCall) {
      this.handlers.onIncomingCall(invitation);
    }
  }

  async disconnect() {
    await this.hangup().catch(() => undefined);
    if (this.registerer) {
      try {
        await this.registerer.unregister();
      } catch {
        // ignore unregister errors during shutdown
      }
      this.registerer = null;
    }
    if (this.userAgent) {
      await this.userAgent.stop();
      this.userAgent = null;
    }
  }

  async dial(phoneNumber) {
    if (!this.userAgent) {
      throw new Error("Browser phone is not connected.");
    }

    // Clear leftover inbound/early/outbound sessions that previously blocked dial.
    await this.clearIdleSession();
    if (isLiveSession(this.session)) {
      if (this.session.state === SessionState.Established) {
        throw new Error("A call is already in progress. Hang up or tap Reset phone first.");
      }
      await this.forceEndSession();
    }
    if (isLiveSession(this.session)) {
      throw new Error("A call is already in progress. Tap Reset phone, then dial again.");
    }

    const target = normalizeDialTarget(phoneNumber, this.config.sipDomain);
    if (!target) {
      throw new Error("Unable to build dial target.");
    }

    // Mic permission before SDP offer (same UX Wave prompts for).
    await primeMicrophoneAccess();

    const inviter = new Inviter(this.userAgent, target);
    applySessionModifiers(inviter);
    this.session = inviter;
    this.attachSessionHandlers(inviter, "OUTBOUND", phoneNumber);
    this.bindSessionHangupHandlers(inviter, "OUTBOUND", phoneNumber);

    await inviter.invite(audioSessionOptions());
    return inviter;
  }

  async acceptCall() {
    if (!this.session) {
      throw new Error("No incoming call to accept.");
    }
    if (!isInvitationAlive(this.session)) {
      throw new Error("Incoming call is no longer available.");
    }

    const invitation = this.session;
    applySessionModifiers(invitation);

    // Mic must be live before PeerConnection so Chrome exposes real LAN host IPs.
    await primeMicrophoneAccess();

    clearBrokenEarlyDialog(invitation);
    await invitation.accept(audioSessionOptions());
    this.bindDialogAckTimeoutHandler(invitation, "INBOUND", null);
  }

  async rejectCall() {
    if (!this.session) return;
    const session = this.session;
    try {
      if (
        session.state === SessionState.Initial ||
        session.state === SessionState.Establishing
      ) {
        await session.reject({ statusCode: 486 });
      } else {
        await this.hangup();
      }
    } finally {
      this.clearSession();
    }
  }

  async hangup() {
    if (!this.session) return;
    const session = this.session;
    try {
      switch (session.state) {
        case SessionState.Initial:
        case SessionState.Establishing:
          if (typeof session.cancel === "function") {
            await session.cancel();
          } else if (typeof session.reject === "function") {
            await session.reject({ statusCode: 486 });
          }
          break;
        case SessionState.Established:
          await session.bye();
          break;
        default:
          break;
      }
    } finally {
      this.clearSession();
      this.stopRemoteAudio();
    }
  }

  setMuted(muted) {
    const peerConnection = this.session?.sessionDescriptionHandler?.peerConnection;
    if (!peerConnection) return false;
    peerConnection.getSenders().forEach((sender) => {
      if (sender.track && sender.track.kind === "audio") {
        sender.track.enabled = !muted;
      }
    });
    return true;
  }

  async setHold(hold) {
    const session = this.session;
    if (!session || session.state !== SessionState.Established) {
      return false;
    }
    if (hold && session.hold) {
      await session.hold();
      return true;
    }
    if (!hold && session.unhold) {
      await session.unhold();
      return true;
    }
    return false;
  }

  attachSessionHandlers(session, direction, phoneNumber) {
    session.stateChange.addListener((state) => {
      if (state === SessionState.Establishing) {
        this.bindPeerConnectionMedia(session, direction);
        this.setupRemoteMedia(session);
        if (this.handlers.onCallProgress) {
          this.handlers.onCallProgress({ direction, phoneNumber, session });
        }
      }
      if (state === SessionState.Established) {
        this.bindPeerConnectionMedia(session, direction);
        this.bindSessionHangupHandlers(session, direction, phoneNumber);
        this.bindDialogAckTimeoutHandler(session, direction, phoneNumber);
        this.setupRemoteMedia(session);
        if (this.handlers.onCallEstablished) {
          this.handlers.onCallEstablished({ direction, phoneNumber, session });
        }
      }
      if (state === SessionState.Terminated) {
        releaseMicrophoneStream();
        this.stopRemoteAudio();
        if (this.handlers.onCallTerminated) {
          this.handlers.onCallTerminated({ direction, phoneNumber, session });
        }
        if (this.session === session) {
          this.session = null;
        }
      }
    });
  }

  bindSessionHangupHandlers(session, direction, phoneNumber) {
    if (!session || session.__browserPhoneHangupBound) {
      return;
    }
    session.__browserPhoneHangupBound = true;
    const previousDelegate = session.delegate || {};
    session.delegate = {
      ...previousDelegate,
      onCancel: (cancel) => {
        if (direction === "INBOUND" && this.handlers.onIncomingCancel) {
          this.handlers.onIncomingCancel({ invitation: session, cancel });
        }
        previousDelegate.onCancel?.(cancel);
      },
      onBye: (bye) => {
        if (this.handlers.onRemoteBye) {
          this.handlers.onRemoteBye({ direction, phoneNumber, session, request: bye });
        }
        previousDelegate.onBye?.(bye);
        bye?.accept?.().catch(() => undefined);
      },
    };
  }

  bindDialogAckTimeoutHandler(session, direction, phoneNumber) {
    const dialog = session?.dialog || session?._dialog;
    if (!dialog || dialog.__browserPhoneAckTimeoutBound) {
      return;
    }
    const previousDelegate = dialog.delegate || {};
    if (!previousDelegate.onAckTimeout) {
      return;
    }
    dialog.__browserPhoneAckTimeoutBound = true;
    dialog.delegate = {
      ...previousDelegate,
      onAckTimeout: () => {
        if (this.handlers.onAckTimeout) {
          this.handlers.onAckTimeout({ direction, phoneNumber, session });
        }
        previousDelegate.onAckTimeout?.();
      },
    };
  }

  bindPeerConnectionMedia(session, direction) {
    const peerConnection = session?.sessionDescriptionHandler?.peerConnection;
    if (!peerConnection || peerConnection.__browserPhoneMediaBound) {
      return;
    }
    peerConnection.__browserPhoneMediaBound = true;
    peerConnection.addEventListener("track", () => {
      this.setupRemoteMedia(session);
    });
    peerConnection.addEventListener("iceconnectionstatechange", () => {
      const state = peerConnection.iceConnectionState;
      if (state === "connected" || state === "completed") {
        this.setupRemoteMedia(session);
        if (this.handlers.onMediaConnected) {
          this.handlers.onMediaConnected({ session, direction, iceState: state });
        }
      }
      if (state === "failed" && this.handlers.onMediaFailure) {
        this.handlers.onMediaFailure({ session, direction, iceState: state });
      }
    });
    peerConnection.addEventListener("connectionstatechange", () => {
      const state = peerConnection.connectionState;
      if (state === "failed" && this.handlers.onMediaFailure) {
        this.handlers.onMediaFailure({ session, direction, iceState: state });
      }
    });
  }

  setupRemoteMedia(session) {
    const audioEl = this.remoteAudio;
    const sdh = session?.sessionDescriptionHandler;
    const peerConnection = sdh?.peerConnection;
    if (!audioEl) return;

    const handlerStream = sdh?.remoteMediaStream;
    if (handlerStream?.getTracks?.().length > 0) {
      audioEl.srcObject = handlerStream;
      audioEl.play().catch(() => undefined);
      return;
    }

    if (!peerConnection) return;

    const remoteStream = new MediaStream();
    peerConnection.getReceivers().forEach((receiver) => {
      if (receiver.track) {
        remoteStream.addTrack(receiver.track);
      }
    });
    if (remoteStream.getTracks().length === 0) {
      return;
    }
    audioEl.srcObject = remoteStream;
    audioEl.play().catch(() => undefined);
  }

  stopRemoteAudio() {
    const audioEl = this.remoteAudio;
    if (!audioEl) return;
    audioEl.srcObject = null;
  }

  clearSession() {
    this.session = null;
  }
}
